import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import {
  activeColumn,
  boldLinkColumn,
  CreateNewEntityButton,
  DynamicFilters,
  Filter,
  PageComponent,
  TableComponent,
  TableProps,
  textColumn,
} from '@dispatch/web/ui';
import { useEffect, useMemo } from 'react';
import {
  Actions,
  AttributeOptions,
  CompanyRole,
  MultiSelectOption,
  Resource,
  ResourceFilterGroup,
  ResourcesListFilter,
  ResultStatus,
  Subjects,
  Team,
} from '@dispatch/shared/types';
import {
  PaginatedQueryResult,
  useFilter,
  useFiltersOrderIndexBulkResourceFilterUpdate,
  useTableState,
} from '@dispatch/shared/hooks';
import { StyledResources } from './ResourcesStyle';
import { useNavigate } from 'react-router-dom';
import { Ability } from '@casl/ability';
import { Button } from '@mui/material';

export interface ResourcesListProps {
  data: PaginatedQueryResult<Resource[], unknown>;
  onSearch: (value: string) => void;
  onFilterChange: (filters: ResourcesListFilter) => void;
  filters: ResourcesListFilter;
  permissions: Ability<[Actions, Subjects]> | undefined;
  handleSort?: (value: GridSortModel) => void;
  companyAttributes: AttributeOptions[];
  filterOptions: Record<string, MultiSelectOption[]>;
  filteredRsrcCounties: MultiSelectOption[];
  filteredRsrcCountiesStatus: ResultStatus;
  setRsrcCountyFilterInput: React.Dispatch<React.SetStateAction<string>>;
  setRsrcStateFilterInput: React.Dispatch<React.SetStateAction<string[]>>;
  storedFilters: ResourcesListFilter[];
  handleSaveFilters: (filters: ResourcesListFilter) => void;
  handleDeleteFilter: (filterId: string) => void;
  handleOrderIndexBulkUpdate: (values: ResourcesListFilter[]) => void;
  handleGroupsOrderIndexBulkUpdate: (values: ResourceFilterGroup[]) => void;
  handleSaveFilterGroup: (values: ResourceFilterGroup) => void;
  handleDeleteFilterGroup: (groupId: string) => void;
}

export function ResourcesList({
  data,
  onSearch,
  onFilterChange,
  filters: listFilters,
  permissions,
  handleSort,
  companyAttributes,
  filterOptions,
  filteredRsrcCounties,
  filteredRsrcCountiesStatus,
  setRsrcCountyFilterInput,
  setRsrcStateFilterInput,
  storedFilters,
  handleSaveFilters,
  handleDeleteFilter,
  handleOrderIndexBulkUpdate,
  handleGroupsOrderIndexBulkUpdate,
  handleSaveFilterGroup,
  handleDeleteFilterGroup,
}: ResourcesListProps) {
  const navigate = useNavigate();
  const { quickFilters, filters, setFilters } = useFilter();
  const { tableState, setTableState } = useTableState();

  useEffect(() => {
    setFilters({ ...listFilters, name: '' });
  }, []);

  const getQuickFilteValue = (filters: ResourcesListFilter): string => {
    if (!filters) return 'all';

    if (filters.isWorkTeam) {
      return 'isworkteam';
    }
    if (filters.isActive === true) {
      return 'active';
    }
    if (filters.isActive === false) {
      return 'inactive';
    }

    return 'all';
  };

  const columns: GridColDef[] = [
    boldLinkColumn<Resource, string>(
      {
        field: 'name',
        headerName: 'Name',
      },
      (resource, _rowValue) => `/resources/${resource?.integrationGUID}?tab=0`
    ),
    textColumn({
      field: 'integrationGUID',
      headerName: 'ID',
      sortable: false,
    }),
    textColumn({
      field: 'role',
      headerName: 'Role',
      valueGetter: (params) =>
        params.row?.company?.role
          ?.filter((c: CompanyRole) => {
            return params.row?.roles?.some(
              (r: { integrationGUID: string }) =>
                r?.integrationGUID?.toLowerCase() ===
                c?.integrationGUID?.toLowerCase()
            );
          })
          .map((role: CompanyRole) => role?.name)
          .join(' '),
      sortable: false,
    }),
    textColumn({
      field: 'team',
      headerName: 'Teams',
      valueGetter: (params) => {
        return params.row?.company?.team?.find((c: Team) => {
          return params.row?.teams?.some(
            (t: { integrationGUID: string }) =>
              t?.integrationGUID?.toLowerCase() ===
              c?.integrationGUID?.toLowerCase()
          );
        })?.name;
      },
      sortable: false,
    }),
    textColumn({
      field: 'state',
      headerName: 'State',
      valueGetter: (params) => params?.row?.locations[0]?.state,
    }),
    activeColumn({
      field: 'isActive',
      headerName: 'Active',
      sortable: false,
    }),
    activeColumn({
      field: 'isWorkTeam',
      headerName: 'Work Team',
      valueGetter: (params) => params?.row?.isWorkTeam,
    }),
  ];

  const tableArgs: TableProps<Resource> = {
    tableState: {
      columnModel: tableState['resources'].columnModel,
      pinnedColumns: {
        ...tableState['resources'].pinnedColumns,
      },
    },
    handleShowColumns: (newModel) => {
      if (!newModel) return;
      setTableState({
        ...tableState,
        resources: { ...tableState['resources'], columnModel: newModel },
      });
    },
    handlePinnedColumns: (newPinned) => {
      setTableState({
        ...tableState,
        resources: { ...tableState['resources'], pinnedColumns: newPinned },
      });
    },
    rows: data?.data || [],
    columns,
    height: 400,
    emptyEntityName: 'Resource',
    status: data?.status,
    getRowId: (row: Resource) => row.integrationGUID,
    serverPaging: {
      ...data,
      onPageSizeChange: (newPageSize: number) => {
        onFilterChange({ ...listFilters, pageSize: newPageSize });
      },
    },
    handleSort,
  };

  const renderActions = () => {
    return (
      permissions?.can?.('upsert', 'resource') && (
        <>
          <Button
            variant={'contained'}
            style={{ backgroundColor: '#2196F3' }}
            onClick={() => navigate('/resources/update-exclusion-zones')}
            sx={{ fontWeight: '700' }}
          >
            Bulk Update
          </Button>
          <CreateNewEntityButton
            onClick={() => navigate('/resources/add')}
            entityName="Resource"
          />
        </>
      )
    );
  };

  const companyAttributeFilters: Filter[] = useMemo(() => {
    return (
      companyAttributes?.map((companyAttribute) => {
        const filter: Filter = {
          id: companyAttribute.title,
          name: companyAttribute.title,
          label: `Resource ${companyAttribute.title}`,
          multi: true,
          type: 'select',
          options:
            companyAttribute.attributeDefinitionValues as MultiSelectOption[],
        };

        return filter;
      }) || []
    );
  }, [companyAttributes]);

  const resourceFilterOptions: Filter[] = [
    {
      type: 'toggle',
      id: 'isStandby',
      name: 'isStandby',
      label: 'Standby Status',
      chipLabel: [
        {
          label: 'Standby',
          value: 'true',
        },
        {
          label: 'Active',
          value: 'false',
        },
      ],
      buttons: [
        {
          label: 'Standby',
          value: true,
        },
        {
          label: 'Active',
          value: false,
        },
      ],
    },
    {
      id: 'resourceStates',
      name: 'resourceStates',
      label: 'State',
      type: 'select',
      multi: true,
      onChange: (value: any) => setRsrcStateFilterInput(value),
      options: filterOptions['states'],
    },
    {
      id: 'resourceCounties',
      name: 'resourceCounties',
      label: 'Counties',
      type: 'async-select',
      multi: true,
      setInput: setRsrcCountyFilterInput,
      options: filteredRsrcCounties,
      loading: filteredRsrcCountiesStatus === 'loading',
    },
    {
      id: 'teamIds',
      name: 'teamIds',
      label: 'Teams',
      type: 'select',
      multi: true,
      options: filterOptions['teams'],
    },
    {
      id: 'groupIds',
      name: 'groupIds',
      label: 'Groups',
      type: 'select',
      multi: true,
      options: filterOptions['groups'],
    },
    {
      id: 'officeIds',
      name: 'officeIds',
      label: 'Offices',
      type: 'select',
      multi: true,
      options: filterOptions['offices'],
    },
    {
      id: 'roleIds',
      name: 'roleIds',
      label: 'Roles',
      type: 'select',
      multi: true,
      options: filterOptions['roles'],
    },
    {
      id: 'employmentType',
      name: 'employmentType',
      label: 'Employment Type',
      type: 'select',
      conditionalRender: {
        parentEquals: 'contingent',
        id: 'contingentCompanies',
        name: 'contingentCompanies',
        label: 'Company',
        type: 'select',
        multi: true,
        required: true,
        options: [
          {
            value: 'pilot',
            title: 'Pilot',
          },
          {
            value: 'crawford',
            title: 'Crawford',
          },
          {
            value: 'other',
            title: 'Other',
          },
        ],
      },
      options: [
        {
          value: 'staff',
          title: 'Staff',
        },
        {
          value: 'contingent',
          title: 'Contingent',
        },
      ],
    },
    ...companyAttributeFilters,
  ];

  const getIsContingent = (employmentType?: string) => {
    if (employmentType === 'contingent') {
      return true;
    } else if (employmentType === 'staff') {
      return false;
    }
    return null;
  };

  useEffect(() => {
    const resourceAttributeFilters = companyAttributeFilters
      .filter((attribute) => {
        return filters?.[attribute.name];
      })
      .map((attribute) => {
        if (filters?.[attribute.name]) {
          return {
            attributeDefinition: attribute.name,
            values: filters?.[attribute.name],
          };
        }
      });

    const filterValue = {
      ...filters,
      pageSize: listFilters.pageSize,
      resourceAttributes: resourceAttributeFilters,
      isActive:
        quickFilters?.isActive === 'all' ||
        quickFilters?.isActive === 'isworkteam'
          ? undefined
          : quickFilters?.isActive === 'active',
      isWorkTeam: quickFilters?.isActive === 'isworkteam' ? true : undefined,
      isContingent: getIsContingent(filters?.employmentType),
    };

    onFilterChange(filterValue);
    data?.goToFirstPage();
  }, [filters, quickFilters]);

  const dynamicFilters: DynamicFilters<ResourcesListFilter> = {
    filters: [{ label: 'Resource', data: resourceFilterOptions }],
    allowSaveFilters: true,
    handleDeleteFilter: handleDeleteFilter,
    handleSaveFilters: handleSaveFilters,
    handleOrderIndexBulkUpdate,
    handleGroupsOrderIndexBulkUpdate,
    handleSaveFilterGroup: handleSaveFilterGroup,
    handleDeleteFilterGroup: handleDeleteFilterGroup,
    multi: true,
    storedFilters: storedFilters,
  };

  return (
    <PageComponent<ResourcesListFilter>
      searchPlaceholder="Name or ID"
      onSearch={onSearch}
      quickFilters={{
        filterKey: 'isActive',
        defaultValue: getQuickFilteValue(listFilters),
        filters: [
          { label: 'All', value: 'all' },
          { label: 'Active', value: 'active' },
          { label: 'Inactive', value: 'inactive' },
          { label: 'Work Team', value: 'isworkteam' },
        ],
      }}
      dynamicFilters={dynamicFilters}
      title="Resources"
      actions={renderActions()}
    >
      <StyledResources>
        <TableComponent<Resource> {...tableArgs} />
      </StyledResources>
    </PageComponent>
  );
}

export default ResourcesList;
